// src/components/manuscripts/ManuscriptCard.jsx
//
// The Manuscripts-tab card, built to the prototype attached to the ticket.
//
// Not DocumentCard. The two look superficially alike and are different things:
// DocumentCard is a landscape card with a full-bleed cover strip, a preview
// paragraph and a reading time, and it is also on EditorHome and DocumentGrid.
// The prototype's card is portrait-thumbnail + status tag + progress footer and
// carries no preview at all. Restyling DocumentCard into this would have
// changed two screens the ticket did not ask about.
import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CalculateIcon from '@mui/icons-material/Calculate';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { formatDate } from '../../utils/editorUtils';
import '../../styles/ManuscriptWorkspace.css';

/**
 * Which pill the card shows.
 *
 * The prototype's three are Draft / Outline / Collaboration. "Outline" has no
 * counterpart in this model -- every row in this tab is already filtered to
 * `status === 'draft'` and there is no outline state to be in -- so the slot
 * carries "Needs Revision" instead, which is a real state this tab has always
 * surfaced (it is what the sidebar's MANUSCRIPTS badge counts).
 */
export function statusTagFor(manuscript, { isCollaboration } = {}) {
  if (manuscript.reviewNotes) {
    return { label: 'Needs Revision', modifier: 'revision', title: manuscript.reviewNotes };
  }
  if (isCollaboration) return { label: 'Collaboration', modifier: 'collab' };
  return { label: 'Draft', modifier: 'draft' };
}

/**
 * The word count, or an em dash.
 *
 * `wordCount` is not a field. Nothing in backend/ writes it and the drafts
 * listing does not return it -- EditorPage computes a live count in the
 * browser and never saves it. DocumentCard had the same `?? 0` fallback, so
 * every card in this tab has always said "0 words", including on a manuscript
 * holding several thousand.
 *
 * Rendering 0 is worse than rendering nothing: it is a specific, wrong number
 * about the user's own work. An em dash says "not known", which is true, and
 * the moment the field lands this starts showing it with no further change.
 */
export function wordCountLabel(manuscript) {
  const n = manuscript?.wordCount;
  return typeof n === 'number' ? `${n.toLocaleString()} words` : '—';
}

/**
 * The items in a manuscript's overflow menu, for whichever handlers are given.
 *
 * Shared by the card's menu and the detail view's More menu so the two cannot
 * disagree on labels, order or the Link/Change Cover wording. Returns an array
 * rather than a component because MUI's Menu does not accept a Fragment (or a
 * wrapper component) as a child. `run` turns a handler into a click handler
 * that closes the menu first.
 */
export function manuscriptMenuItems(
  manuscript,
  { onOpenStructure, onOpenPricing, onLinkCover, onUnlinkCover, onDelete },
  run
) {
  const items = [];
  if (onOpenStructure) {
    items.push(
      <MenuItem key="structure" onClick={run(onOpenStructure)}>
        <AutoStoriesIcon fontSize="small" sx={{ mr: 1 }} />
        Manuscript Structure
      </MenuItem>
    );
  }
  if (onOpenPricing) {
    items.push(
      <MenuItem key="pricing" onClick={run(onOpenPricing)}>
        <CalculateIcon fontSize="small" sx={{ mr: 1 }} />
        Book Pricing Estimator
      </MenuItem>
    );
  }
  if ((onOpenStructure || onOpenPricing) && (onLinkCover || onUnlinkCover || onDelete)) {
    items.push(<Divider key="divider-1" />);
  }
  if (onLinkCover) {
    items.push(
      <MenuItem key="link" onClick={run(onLinkCover)}>
        <LinkIcon fontSize="small" sx={{ mr: 1 }} />
        {manuscript.linkedCoverId ? 'Change Cover Link' : 'Link to Cover'}
      </MenuItem>
    );
  }
  if (onUnlinkCover && manuscript.linkedCoverId) {
    items.push(
      <MenuItem key="unlink" onClick={run(onUnlinkCover)}>
        <LinkOffIcon fontSize="small" sx={{ mr: 1 }} />
        Unlink Cover
      </MenuItem>
    );
  }
  if ((onLinkCover || onUnlinkCover) && onDelete) items.push(<Divider key="divider-2" />);
  if (onDelete) {
    items.push(
      <MenuItem key="delete" onClick={run(onDelete)}>
        Delete
      </MenuItem>
    );
  }
  return items;
}

export default function ManuscriptCard({
  manuscript,
  isCollaboration = false,
  onOpen,
  onDelete,
  onLinkCover,
  onUnlinkCover,
  onOpenStructure,
  onOpenPricing,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // A listing presigns cover URLs, so they expire. A dead one falls back to the
  // placeholder rather than leaving a broken image in a grid of cards.
  const [coverFailed, setCoverFailed] = useState(false);

  const coverUrl = !coverFailed ? manuscript.cover_url || '' : '';
  const tag = statusTagFor(manuscript, { isCollaboration });
  const progress = manuscript.taskProgress || null;
  const hasMenu = onDelete || onLinkCover || onUnlinkCover || onOpenStructure || onOpenPricing;

  const stop = (e) => e.stopPropagation();
  const closeMenu = () => setAnchorEl(null);
  const runAndClose = (fn) => (e) => {
    e.stopPropagation();
    closeMenu();
    fn?.(manuscript._id);
  };

  const pill = (
    <span className={`rv-status-tag rv-status-tag--${tag.modifier}`}>{tag.label}</span>
  );

  return (
    <div
      className="rv-manuscript-card"
      data-testid="manuscript-card"
      data-manuscript-id={manuscript._id}
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(manuscript)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(manuscript);
        }
      }}
    >
      <div className="rv-manuscript-card__top">
        {coverUrl ? (
          <img
            className="rv-manuscript-card__thumb"
            src={coverUrl}
            alt={
              manuscript.linkedCoverTitle
                ? `Cover: ${manuscript.linkedCoverTitle}`
                : `Cover of ${manuscript.title || 'Untitled'}`
            }
            onError={() => setCoverFailed(true)}
          />
        ) : (
          <div
            className="rv-manuscript-card__thumb rv-manuscript-card__thumb--empty"
            aria-hidden="true"
          >
            <MenuBookIcon fontSize="small" />
          </div>
        )}

        <div className="rv-manuscript-card__meta">
          <div className="rv-manuscript-card__title-row">
            <h3 className="rv-manuscript-card__title">{manuscript.title || 'Untitled'}</h3>
            {hasMenu && (
              <>
                <IconButton
                  size="small"
                  data-testid="manuscript-card-menu"
                  aria-label={`Actions for ${manuscript.title || 'Untitled'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={closeMenu}
                  onClick={stop}
                >
                  {manuscriptMenuItems(
                    manuscript,
                    { onOpenStructure, onOpenPricing, onLinkCover, onUnlinkCover, onDelete },
                    runAndClose
                  )}
                </Menu>
              </>
            )}
          </div>

          {tag.title ? (
            <Tooltip title={tag.title} placement="top" arrow>
              {pill}
            </Tooltip>
          ) : (
            pill
          )}

          <span className="rv-manuscript-card__edited">
            Last edited: {formatDate(manuscript.lastEdited)}
          </span>
        </div>
      </div>

      {/* The prototype pairs words with a completion percentage. See
          `_attach_task_progress` in backend/routes/drafts.py for where the
          percentage comes from and why it is not words-against-a-target: there
          is no word target in this model. A manuscript with no milestone plan
          shows its word count alone -- an empty bar would claim "planned,
          nothing done", which is a different thing from "not planned". */}
      <div className="rv-manuscript-card__footer">
        <div
          className={`rv-manuscript-card__stats${
            progress ? '' : ' rv-manuscript-card__stats--absent'
          }`}
        >
          <span>{wordCountLabel(manuscript)}</span>
          <span>{progress ? `${progress.percent}%` : '—'}</span>
        </div>
        {progress && (
          <div
            className="rv-progress-track"
            role="progressbar"
            aria-label="Tasks completed"
            aria-valuenow={progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="rv-progress-track__fill" style={{ width: `${progress.percent}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}
